import * as base from "./lib/baseActions";
import { jwtData } from "lib/config";
import { UserInfo } from "context/user";
import { LoginErrors } from "pages/Login";
import { RoleType } from "actions/roleActions";
import { CreatePasswordErrors } from "pages/CreatePassword";
import { ForgotPasswordErrors } from "pages/Login/ForgotPassword";
import { TwoFAFormErrors } from "pages/TwoFA";

export interface loginAdminData {
  email?: string;
  password?: string;
}

export interface createPasswordData {
  password?: string;
  confirmPassword?: string;
}

export interface twoFAData {
  email?: string;
  twoFaCode?: string;
}

export interface forgotPasswordData {
  email: string;
}

interface authTokenPayload {
  auth: {
    token?: string;
    expiresAt?: string | number;
  };
  refresh: {
    token?: string;
    expiresAt?: string | number;
  };
}

const fetchAdminPanelUser = () => {
  return base.get("admin/me");
};

const loginAdminPanelUser = async (
  data: loginAdminData
): Promise<base.DataWrapper<UserInfo & RoleType>> => {
  const { email, password } = data;

  try {
    const adminPanelUser = await base.post("adminLogin", { email, password });
    return adminPanelUser;
  } catch (err) {
    if (err?.original?.response?.status === 400) {
      throw base.customizeError(err, LoginErrors.InvalidCredentials);
    } else {
      throw base.customizeError(err, LoginErrors.General);
    }
  }
};

const logoutUser = () => {
    localStorage.removeItem(jwtData.authToken);
    localStorage.removeItem(jwtData.refreshToken);
    localStorage.removeItem(jwtData.tokenExpiry);
    localStorage.removeItem(jwtData.refreshExpiry);
};

const sendForgotPasswordEmail = async (data: forgotPasswordData) => {
  const { email } = data;

  try {
    const forgotPasswordResult = await base.put(`users/reset`, { email });
    return forgotPasswordResult;
  } catch (err) {
    if (err?.original?.response?.status === 400) {
      if (err?.original?.response?.data?.id?.includes("unknownUser")) {
        throw base.customizeError(err, ForgotPasswordErrors.UserDoesNotExist);
      }
      throw base.customizeError(err, ForgotPasswordErrors.General);
    }
  }
};

const resetPassword = async (
  data: createPasswordData,
  resetKey?: string
): Promise<base.DataWrapper<UserInfo & RoleType>> => {
  const { password, confirmPassword } = data;

  try {
    const updatedUserResult = await base.put(`passwordReset/${resetKey}`, {
      password,
      confirmPassword,
    });
    return updatedUserResult;
  } catch (err) {
    throw base.customizeError(err, CreatePasswordErrors.General);
  }
};

const userIsAuthorized = () => {
  const initialAuthToken = localStorage?.getItem(jwtData.authToken);
  return initialAuthToken ? true : false;
};

const persistAuthTokens = (authPayload: authTokenPayload) => {
  if (authPayload?.auth?.token) {
    localStorage.setItem(jwtData.authToken, authPayload.auth.token);
  }
  if (authPayload?.refresh?.token) {
    localStorage.setItem(jwtData.refreshToken, authPayload.refresh.token);
  }
  if (authPayload?.auth?.expiresAt != null) {
    localStorage.setItem(jwtData.tokenExpiry, String(authPayload.auth.expiresAt));
  }
  if (authPayload?.refresh?.expiresAt != null) {
    localStorage.setItem(
      jwtData.refreshExpiry,
      String(authPayload.refresh.expiresAt)
    );
  }
};

const verify2FACode = async (
  data: twoFAData
): Promise<base.DataWrapper<UserInfo & RoleType>> => {
  const { email, twoFaCode } = data;

  try {
    const loginResult = await base.post("validate2Fa", { email, twoFaCode });
    const authPayload = loginResult.data as authTokenPayload;
    persistAuthTokens(authPayload);
    return fetchAdminPanelUser();
  } catch (err) {
    if (err?.original?.response?.status === 400) {
      throw base.customizeError(err, TwoFAFormErrors.Invalid2FACode);
    } else {
      throw base.customizeError(err, TwoFAFormErrors.General);
    }
  }
};

export {
  fetchAdminPanelUser,
  loginAdminPanelUser,
  logoutUser,
  userIsAuthorized,
  verify2FACode,
  sendForgotPasswordEmail,
  resetPassword,
};
